import { ServiceStoreConfig } from "../types/ServiceStoreTypes";

/**
 * Admin module ServiceStores (api-keys, audit-log, data-export).
 *
 * Declares the ServiceStores backing the admin views (migrated off the removed
 * `createRestApi`). ViewStores read `rootStore.<name>Store.entities` and call
 * `.find/.create/.remove` instead.
 *
 * - `api-keys`   custom admin-only CRUD service; rows keyed by numeric `id`.
 * - `audit-log`  server-paged, read-only viewer; the ViewStore reads the find
 *                RESPONSE (current page + total), so the entity cache is
 *                incidental — `id` is still the stable row key.
 * - `data-export` action-only service (`create({ action: 'counts' | 'export' })`)
 *                 returning a one-off result bundle, not entities; keyed by `id`
 *                 nominally since it is never list-cached.
 */
export const adminStores: ServiceStoreConfig[] = [
  // throwOnError: ApiKeysStore (its only reader) catches every call and shows
  // the reason; with the default a refused create/patch resolved null and the
  // screen reported success (Codex review 2026-09-25, the mint-only scope).
  { name: "api-keys", options: { idField: "id", enableStorage: false, throwOnError: true } },
  { name: "audit-log", options: { idField: "id", enableStorage: false } },
  { name: "data-export", options: { idField: "id", enableStorage: false } },
  // Admin → Import trackers: the committed runs (find). A dry run or an import
  // is a create through the RAW Feathers service — its answer is a report about
  // a file, not a run to cache as one.
  {
    name: "tracker-imports",
    options: { idField: "id", enableStorage: false, enableOffline: false, disableInitialFind: true, methods: ["find"] },
  },
  // `integration-errors` admin-only viewer: find() resolves a BARE ARRAY
  // (newest first, not paginated); the ViewStore reads it as a working snapshot
  // and filters client-side. Rows are keyed by string `id`.
  { name: "integration-errors", options: { idField: "id", enableStorage: false } },
  // `data-integrity` is a find-only service whose find() resolves a BARE report
  // object (no `{ data, total }` envelope) and is consumed directly by its Body
  // component. enableOffline OFF so the plain ServiceStore returns the object
  // verbatim — the offline wrapper would null it and crash in cacheResults,
  // blanking the report.
  //
  // `system-health` is deliberately NOT re-declared here: it is configured once
  // in system.stores.ts. Two configs deriving the same `systemHealthStore` key
  // is a store-key collision — harmless while the policy is 'first', a hard
  // error the moment `onDuplicateStore: 'throw'` is set.
  {
    name: "data-integrity",
    options: { idField: "id", enableStorage: false, enableOffline: false },
  },
  // Admin → Email (views/admin/Email). `email-config` is find-only and resolves
  // a BARE state object — enableOffline OFF for the same reason as
  // data-integrity above. `email-sends` is the paged `email_sends` ledger
  // ({ data, total }; the ViewStore reads the find RESPONSE, as AuditLogStore
  // does) plus a `create({ test: true })` that mails the caller — sent through
  // the RAW client (EmailAdminStore.sendTest), because its `{ outcome, send }`
  // answer has no id and this wrapper would cache it as "undefined". Offline OFF:
  // it is an admin audit read with no use offline, and a reconnect resync of an
  // unbounded ledger is load for nothing. Neither is ever stored.
  {
    name: "email-config",
    options: { idField: "id", enableStorage: false, enableOffline: false },
  },
  {
    name: "email-sends",
    options: { idField: "id", enableStorage: false, enableOffline: false },
  },
];
